// src/helpers/pdfCarteraCheques.js
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  fmtDate,
  fmtNum,
  S,
  loadImageAsDataURL,
  drawHeader,
} from "./pdfCommonSanPancracio";

const $ = (n) => fmtNum(n);

const DEFAULT_LOGO_URL = "/SanPancracio.png";
const DEFAULT_EMPRESA = {
  nombre: "SAN PANCRACIO SRL",
  linea1: "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
  linea2: "Tel: 0381-3008829 • contacto@sanpancracio.com",
};

const safeDate = (d) => (d ? fmtDate(d) : "");

export async function generarPDFCarteraCheques(
  rows = [],
  filtros = {},
  opts = {}
) {
  console.log("ENTRO generarPDFCarteraCheques, rows:", rows?.length, "filtros:", filtros);
  const logoUrl = opts.logoUrl || DEFAULT_LOGO_URL;
  const empresa = { ...DEFAULT_EMPRESA, ...(opts.empresa || {}) };

  let logoDataURL = null;
  try {
    logoDataURL = await loadImageAsDataURL(logoUrl);
  } catch (e) {
    console.warn("No pude cargar logo:", e?.message || e);
  }

  // ✅ A4 apaisado
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const M_LEFT = 10;
  const M_RIGHT = 10;
  const yTop = 10;

  // Header con solo nombre (como venimos haciendo)
  const empresaSoloNombre = { ...empresa, linea1: "", linea2: "" };
  drawHeader(doc, yTop, logoDataURL, empresaSoloNombre);

  // Linea1/Linea2 centradas
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (empresa.linea1) doc.text(empresa.linea1, 148.5, yTop + 12, { align: "center" });
  if (empresa.linea2) doc.text(empresa.linea2, 148.5, yTop + 17, { align: "center" });

  let y = yTop + 34;

  // Título
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("CARTERA DE CHEQUES / ECHEQ", 148.5, y, { align: "center" });
  y += 7;

  // Subtítulo (filtros)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const sub = [
    filtros?.estado ? `Estado: ${filtros.estado}` : null,
    filtros?.tipo ? `Tipo: ${filtros.tipo}` : null,
    filtros?.soloDisponibles ? "Sólo disponibles (EN_CARTERA)" : null,
  ].filter(Boolean).join("  |  ");

  if (sub) {
    doc.text(sub, 148.5, y, { align: "center" });
    y += 6;
  } else {
    y += 2;
  }

  // ✅ Tabla: NO DOM, NO “texto vertical”, todo controlado
  autoTable(doc, {
    startY: y,
    margin: { left: M_LEFT, right: M_RIGHT },
    head: [[
      "Tipo",
      "Estado",
      "Importe",
      "Recibido",
      "F. Cheque",
      "F. Pago",
      "Nro",
      "Banco emisor",
      "Cobrado a",
      "CUIT Cobrado",
      "Pagado a",
      "CUIT Pagado",
      "Obs.",
      "Cliente",
      "Origen",
    ]],
    body: rows.map((r) => ([
      String(r?.tipo || "").toUpperCase(),
      String(r?.estado || "").toUpperCase(),
      `$ ${$(r?.importe)}`,
      safeDate(r?.recibidoEn),
      safeDate(r?.fechaCheque),
      safeDate(r?.fechaPago),
      S(r?.nro || r?.nroCheque || r?.idICheq || ""),
      S(r?.bancoEmisor || ""),
      S(r?.cobradoA || r?.librador || r?.emisor || ""),
      S(r?.cuitCobradoA || r?.cuitLibrador || r?.cuitEmisor || ""),
      S(r?.pagadoA || r?.beneficiario || ""),
      S(r?.cuitPagadoA || r?.cuitBeneficiario || ""),
      S(r?.observaciones || ""),
      S(r?.cliente?.razonsocial || r?.clienteNombre || ""),
      S(r?.origenTipo || r?.origen || ""),
    ])),
    styles: {
      fontSize: 7,
      cellPadding: 1.2,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      fillColor: [51, 122, 183],
      textColor: 255,
    },

    // ✅ ESTA ES LA CLAVE: anchos (en mm) + alineaciones
    columnStyles: {
      0:  { cellWidth: 15 },                  // Tipo
      1:  { cellWidth: 20 },                  // Estado
      2:  { cellWidth: 20, halign: "right" }, // Importe
      3:  { cellWidth: 15, halign: "center" },// Recibido
      4:  { cellWidth: 14, halign: "center" },// F. Cheque
      5:  { cellWidth: 14, halign: "center" },// F. Pago
      6:  { cellWidth: 16 },                  // Nro
      7:  { cellWidth: 18 },                  // Banco
      8:  { cellWidth: 26 },                  // Cobrado a
      9:  { cellWidth: 18 },                  // CUIT Cobrado
      10: { cellWidth: 22 },                  // Pagado a
      11: { cellWidth: 18 },                  // CUIT Pagado
      12: { cellWidth: 20 },                  // Obs.
      13: { cellWidth: 20 },                  // Cliente
      14: { cellWidth: 20 },                  // Origen
    },

    // Footer con páginas
    didDrawPage: (d) => {
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      doc.setDrawColor(200);
      doc.line(M_LEFT, pageH - 12, pageW - M_RIGHT, pageH - 12);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(
        "Documento electrónico generado por el sistema San Pancracio — Cartera",
        M_LEFT,
        pageH - 7
      );
      doc.text(
        `Página ${d.pageNumber}`,
        pageW - M_RIGHT,
        pageH - 7,
        { align: "right" }
      );
    },
  });

  doc.save(`cartera-cheques-${new Date().toISOString().slice(0, 10)}.pdf`);
}